"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get AdhocFormAssignmentInput () {
        return AdhocFormAssignmentInput;
    },
    get DirectExternalFormAssignment () {
        return DirectExternalFormAssignment;
    },
    get DirectExternalFormAssignmentInput () {
        return DirectExternalFormAssignmentInput;
    },
    get DirectFormAssignment () {
        return DirectFormAssignment;
    },
    get DirectFormAssignmentInput () {
        return DirectFormAssignmentInput;
    },
    get DirectInternalFormAssignment () {
        return DirectInternalFormAssignment;
    },
    get DirectInternalFormAssignmentInput () {
        return DirectInternalFormAssignmentInput;
    },
    get EasyAddFormConfig () {
        return EasyAddFormConfig;
    },
    get EasyAddFormConfigInput () {
        return EasyAddFormConfigInput;
    },
    get EasyAddFormConfigInputBase () {
        return EasyAddFormConfigInputBase;
    },
    get ExternalAuthor () {
        return ExternalAuthor;
    },
    get ExternalAuthorInput () {
        return ExternalAuthorInput;
    },
    get NamedSigner () {
        return NamedSigner;
    },
    get ReviewHoldConfig () {
        return ReviewHoldConfig;
    },
    get TemplateEasyAddFormConfig () {
        return TemplateEasyAddFormConfig;
    },
    get TemplateEasyAddFormConfigInput () {
        return TemplateEasyAddFormConfigInput;
    },
    get TemplateExternalFormAssignment () {
        return TemplateExternalFormAssignment;
    },
    get TemplateExternalFormAssignmentInput () {
        return TemplateExternalFormAssignmentInput;
    },
    get TemplateFormAssignment () {
        return TemplateFormAssignment;
    },
    get TemplateFormAssignmentInput () {
        return TemplateFormAssignmentInput;
    },
    get TemplateFormAssignmentWithNames () {
        return TemplateFormAssignmentWithNames;
    },
    get TemplateInternalFormAssignment () {
        return TemplateInternalFormAssignment;
    },
    get TemplateInternalFormAssignmentInput () {
        return TemplateInternalFormAssignmentInput;
    }
});
const _zod = require("zod");
const _models = require("@performyard/models");
const _utilsisomorphic = require("@performyard/utils-isomorphic");
const ExternalAuthor = _zod.z.object({
    email: _models.ExternalReviewAuthor.shape.email,
    emailBody: _models.ExternalReviewAuthor.shape["email-body"],
    name: _models.ExternalReviewAuthor.shape.name
});
const ExternalAuthorInput = _zod.z.object({
    email: ExternalAuthor.shape.email.email(),
    emailBody: ExternalAuthor.shape.emailBody.max(2000),
    name: ExternalAuthor.shape.name.max(250)
});
const ReviewHoldConfig = _zod.z.object({
    enabled: _models.ReviewHoldConfigModel.shape.enabled,
    placement: _zod.z.object({
        phase: _models.ReviewHoldPlacementModel.shape.phase,
        index: _models.ReviewHoldPlacementModel.shape.index
    })
});
const EasyAddFormConfig = _zod.z.object({
    formBaseId: _utilsisomorphic.transformObjectId,
    redacted: _models.EasyAddReviewTemplateFormModel.shape.redacted,
    visible: _models.EasyAddReviewTemplateFormModel.shape.visible,
    easyAdd: _models.EasyAddReviewTemplateFormModel.shape.easy_add,
    whoCanEasyAdd: _models.EasyAddReviewTemplateFormModel.shape.who_can_easy_add.unwrap(),
    subjectEmployeeChooseAuthor: _models.EasyAddReviewTemplateFormModel.shape.subject_employee_choose_author,
    allowComments: _models.EasyAddReviewTemplateFormModel.shape.allow_comments,
    /* Relative authors due date, calculated from the date when the form is added */ daysToCompleteAuthors: _models.EasyAddReviewTemplateFormModel.shape.days_to_complete_authors.optional(),
    /* Static authors due date - Review Cycles only */ authorsDueDate: _utilsisomorphic.transformDate.optional(),
    /** If signers are set there must be signer due dates */ signers: _zod.z.array(_models.ReviewAssignmentSigner).optional(),
    /* Relative signers due date, calculated from the date when the form is added, if signers is provided */ daysToCompleteSigners: _models.EasyAddReviewTemplateFormModel.shape.days_to_complete_signers.optional(),
    /* Static signers due date, if signers is provided - Review Cycles only */ signersDueDate: _utilsisomorphic.transformDate.optional(),
    isActive: _models.EasyAddReviewTemplateFormModel.shape.is_active,
    requiresApproval: _models.EasyAddReviewTemplateFormModel.shape.requires_approval
});
const EasyAddFormConfigInputBase = EasyAddFormConfig.extend({
    redacted: _zod.z.boolean().default(false),
    visible: _zod.z.boolean().default(false),
    daysToCompleteAuthors: _zod.z.coerce.number().int().nonnegative().optional(),
    daysToCompleteSigners: _zod.z.coerce.number().int().nonnegative().optional()
});
const EasyAddFormConfigInput = EasyAddFormConfigInputBase.refine((d)=>{
    const hasRelativeAuthor = d.daysToCompleteAuthors !== undefined;
    const hasStaticAuthor = d.authorsDueDate !== undefined;
    // Exactly one author due date mode required (not both, not neither)
    return hasRelativeAuthor !== hasStaticAuthor;
}, {
    message: "Must provide exactly one of daysToCompleteAuthors (relative) or authorsDueDate (static)"
}).refine((d)=>{
    const hasSigners = d.signers !== undefined && d.signers.length > 0;
    const hasSignerDueDate = d.daysToCompleteSigners !== undefined || d.signersDueDate !== undefined;
    return hasSigners === hasSignerDueDate;
}, {
    message: "Signers and signer due dates must be provided together"
}).refine((d)=>{
    const hasSigners = d.signers !== undefined && d.signers.length > 0;
    if (!hasSigners) return true;
    const authorIsRelative = d.daysToCompleteAuthors !== undefined;
    const signerIsRelative = d.daysToCompleteSigners !== undefined;
    return authorIsRelative === signerIsRelative;
}, {
    message: "Signer due date mode must match author due date mode (both relative or both static)"
});
// ============================================================================
// Template Form Assignments — These will be used to generate Direct Form Assignments,
// and create review reports at some point
// ============================================================================
const BaseTemplateFormAssignment = _zod.z.object({
    /**
	 * The unique form assignment ID used to reference how this form was configured
	 * for assignment in the template
	 */ id: _utilsisomorphic.transformObjectId,
    formBaseId: _utilsisomorphic.transformObjectId,
    redacted: _models.BaseReviewTemplateFormModel.shape.redacted,
    visible: _models.BaseReviewTemplateFormModel.shape.visible
});
const TemplateInternalFormAssignment = BaseTemplateFormAssignment.extend({
    basic: _zod.z.literal(true),
    authors: _zod.z.array(_models.ReviewAssignmentAuthor),
    daysToCompleteAuthors: _models.InternalReviewTemplateFormModel.shape.days_to_complete_authors,
    signers: _zod.z.array(_models.ReviewAssignmentSigner).optional(),
    daysToCompleteSigners: _models.InternalReviewTemplateFormModel.shape.days_to_complete_signers.optional(),
    hold: ReviewHoldConfig.optional()
});
const TemplateExternalFormAssignment = BaseTemplateFormAssignment.extend({
    external: _zod.z.literal(true),
    authors: _zod.z.array(ExternalAuthor.partial()).optional()
});
const TemplateEasyAddFormConfig = BaseTemplateFormAssignment.extend(EasyAddFormConfig.omit({
    authorsDueDate: true,
    signersDueDate: true
}).shape);
const TemplateFormAssignment = _zod.z.union([
    TemplateEasyAddFormConfig,
    TemplateInternalFormAssignment,
    TemplateExternalFormAssignment
]);
// ============================================================================
// Template Form Assignments — response shape with resolved form/user names
// ============================================================================
/** Participant value paired with its resolved display name. Response-only. */ const NamedAuthor = _zod.z.object({
    id: _models.ReviewAssignmentAuthor,
    name: _zod.z.string()
});
const NamedSigner = _zod.z.object({
    id: _models.ReviewAssignmentSigner,
    name: _zod.z.string()
});
const TemplateInternalFormAssignmentWithNames = TemplateInternalFormAssignment.extend({
    formName: _zod.z.string().optional(),
    authors: _zod.z.array(NamedAuthor),
    signers: _zod.z.array(NamedSigner).optional()
});
const TemplateExternalFormAssignmentWithNames = TemplateExternalFormAssignment.extend({
    formName: _zod.z.string().optional()
});
const TemplateEasyAddFormConfigWithNames = TemplateEasyAddFormConfig.extend({
    formName: _zod.z.string().optional(),
    signers: _zod.z.array(NamedSigner).optional()
});
const TemplateFormAssignmentWithNames = _zod.z.union([
    TemplateEasyAddFormConfigWithNames,
    TemplateInternalFormAssignmentWithNames,
    TemplateExternalFormAssignmentWithNames
]);
// ============================================================================
// Template Form Assignments — input (request validation)
// ============================================================================
const BaseTemplateFormAssignmentInput = BaseTemplateFormAssignment.extend({
    id: _utilsisomorphic.transformObjectId.nullish(),
    redacted: _models.BaseReviewTemplateFormModel.shape.redacted.default(false),
    visible: _models.BaseReviewTemplateFormModel.shape.visible.default(false)
});
const TemplateInternalFormAssignmentInput = BaseTemplateFormAssignmentInput.extend({
    basic: _zod.z.literal(true),
    authors: _zod.z.array(_models.ReviewAssignmentAuthor).min(1, "At least one author must be selected"),
    daysToCompleteAuthors: _zod.z.coerce.number().int().nonnegative(),
    signers: _zod.z.array(_models.ReviewAssignmentSigner).optional(),
    daysToCompleteSigners: _zod.z.coerce.number().int().nonnegative().optional()
});
const TemplateExternalFormAssignmentInput = BaseTemplateFormAssignmentInput.extend({
    external: _zod.z.literal(true),
    authors: _zod.z.array(ExternalAuthorInput.partial()).optional()
});
const TemplateEasyAddFormConfigInput = EasyAddFormConfig.omit({
    authorsDueDate: true,
    signersDueDate: true
}).extend({
    redacted: _zod.z.boolean().default(false),
    visible: _zod.z.boolean().default(false),
    daysToCompleteAuthors: _zod.z.coerce.number().int().nonnegative(),
    daysToCompleteSigners: _zod.z.coerce.number().int().nonnegative().optional()
}).safeExtend(BaseTemplateFormAssignmentInput.shape).refine((d)=>{
    const hasSigners = d.signers !== undefined && d.signers.length > 0;
    const hasSignerDueDate = d.daysToCompleteSigners !== undefined;
    return hasSigners === hasSignerDueDate;
}, {
    message: "Signers and signer due dates must be provided together"
});
const TemplateFormAssignmentInput = _zod.z.union([
    TemplateEasyAddFormConfigInput,
    TemplateInternalFormAssignmentInput,
    TemplateExternalFormAssignmentInput
]);
// ============================================================================
// Direct Form Assignments — These are being used to create review reports right now
// ============================================================================
const BaseDirectFormAssignment = _zod.z.object({
    /** The unique form assignment ID used to reference how this form was configured for assignment */ id: _utilsisomorphic.transformObjectId.optional(),
    formBaseId: _utilsisomorphic.transformObjectId,
    redacted: _models.InternalReviewTemplateFormModel.shape.redacted,
    visible: _models.InternalReviewTemplateFormModel.shape.visible
});
const DirectInternalFormAssignment = BaseDirectFormAssignment.extend({
    basic: _zod.z.literal(true),
    authors: _zod.z.array(_models.ReviewAssignmentAuthor),
    authorsDueDate: _utilsisomorphic.transformDate,
    signers: _zod.z.array(_models.ReviewAssignmentSigner).optional(),
    signersDueDate: _utilsisomorphic.transformDate.optional(),
    hold: ReviewHoldConfig.optional()
});
const DirectExternalFormAssignment = BaseDirectFormAssignment.extend({
    external: _zod.z.literal(true),
    authors: _zod.z.array(ExternalAuthor.partial()).optional()
});
const DirectFormAssignment = _zod.z.union([
    DirectInternalFormAssignment,
    DirectExternalFormAssignment
]);
// ============================================================================
// Direct Form Assignments — input (request validation)
// ============================================================================
const BaseDirectFormAssignmentInput = BaseDirectFormAssignment.extend({
    redacted: _zod.z.boolean().default(false),
    visible: _zod.z.boolean().default(false)
});
const DirectInternalFormAssignmentInput = BaseDirectFormAssignmentInput.extend({
    basic: _zod.z.literal(true),
    authors: _zod.z.array(_models.ReviewAssignmentAuthor).min(1, "At least one author must be selected"),
    authorsDueDate: _utilsisomorphic.transformDate,
    signers: _zod.z.array(_models.ReviewAssignmentSigner).optional(),
    signersDueDate: _utilsisomorphic.transformDate.optional()
});
const DirectExternalFormAssignmentInput = BaseDirectFormAssignmentInput.extend({
    external: _zod.z.literal(true),
    authors: _zod.z.array(ExternalAuthorInput.partial()).optional()
});
const DirectFormAssignmentInput = _zod.z.union([
    DirectInternalFormAssignmentInput,
    DirectExternalFormAssignmentInput
]);
// ============================================================================
// Ad hoc Form Assignments — input for reviews created outside a review cycle
// ============================================================================
const AdhocInternalFormAssignmentInput = DirectInternalFormAssignmentInput.extend({
    hold: _zod.z.undefined({
        message: "Hold is not available for reviews created outside a review cycle"
    }).optional()
});
const AdhocFormAssignmentInput = _zod.z.union([
    AdhocInternalFormAssignmentInput,
    DirectExternalFormAssignmentInput
]);
